<div class="place-collector-container">
  <div class="header">
    <div class="header-content">
      <div>
        <h1>Marketing Leads</h1>
        <p>Run jobs to fetch places using Google API</p>
      </div>
    </div>
  </div>

  <!-- Error Message -->
  <div class="error-message" *ngIf="errorMessage">
    <i class="fas fa-exclamation-circle"></i> {{ errorMessage }}
  </div>

  <!-- Success Message -->
  <div class="success-message" *ngIf="successMessage">
    <i class="fas fa-check-circle"></i> {{ successMessage }}
  </div>

  <div class="content-grid">
    <!-- Collection Form — only visible to users with Marketing_Manage (Telemarketer / CCAdmin) -->
    <div class="form-section" *appHasPermission="['Marketing_Manage', 'Telemarketer_Permissions']">
      <div class="section-header">
        <h3><i class="fas fa-play-circle"></i> Start Collection Job</h3>
      </div>

      <div class="form-container">
        <form (ngSubmit)="startCollection()">
          <div class="form-group">
            <label for="placeType">Place Type <span class="required">*</span></label>
            <select id="placeType" [(ngModel)]="formData.placeType" name="placeType" class="form-control" required>
              <option value="">Select Place Type</option>
              <optgroup *ngFor="let group of groupedPlaceTypes" [label]="group.category">
                <option *ngFor="let placeType of group.types" [value]="placeType.name">
                  {{ placeType.displayName || placeType.name }}
                </option>
              </optgroup>
            </select>
            <div *ngIf="loadingPlaceTypes" class="loading-place-types">
              <i class="fas fa-spinner fa-spin"></i> Loading place types...
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="latitude">Latitude <span class="required">*</span></label>
              <input type="number" id="latitude" [(ngModel)]="formData.latitude" name="latitude" class="form-control"
                step="any" placeholder="e.g., 8.7128" required>
            </div>

            <div class="form-group">
              <label for="longitude">Longitude <span class="required">*</span></label>
              <input type="number" id="longitude" [(ngModel)]="formData.longitude" name="longitude" class="form-control"
                step="any" placeholder="e.g., 76.0060" required>
            </div>
          </div>

          <div class="form-group">
            <label for="radiusMeters">Radius (meters) <span class="required">*</span></label>
            <input type="number" id="radiusMeters" [(ngModel)]="formData.radiusMeters" name="radiusMeters"
              class="form-control" min="1" placeholder="e.g., 5000" required>
            <small class="form-hint">Search radius in meters from the specified location</small>
          </div>

          <div class="form-group">
            <label for="query">Query (Optional)</label>
            <input type="text" id="query" [(ngModel)]="formData.query" name="query" class="form-control"
              placeholder="e.g., restaurant, hotel, etc.">
            <small class="form-hint">Additional search query to filter results</small>
          </div>

          <div class="form-group checkbox-group">
            <label class="checkbox-label">
              <input type="checkbox" [(ngModel)]="formData.fetchAllPages" name="fetchAllPages">
              <span>Fetch All Pages</span>
            </label>
            <small class="form-hint">If checked, will fetch all available pages of results</small>
          </div>

          <div class="form-group checkbox-group">
            <label class="checkbox-label">
              <input type="checkbox" [(ngModel)]="formData.forceRefresh" name="forceRefresh">
              <span>Force Refresh</span>
            </label>
            <small class="form-hint">If checked, will refresh existing places even if they already exist</small>
          </div>

          <div class="form-actions">
            <button type="submit" class="btn btn-primary" [disabled]="loading || loadingPlaceTypes">
              <i class="fas fa-spinner fa-spin" *ngIf="loading"></i>
              <i class="fas fa-play" *ngIf="!loading"></i>
              {{ loading ? 'Starting...' : 'Start Collection' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Read-only notice for Marketers (Marketing_View but NOT Marketing_Manage) -->
    <div class="form-section" *ngIf="!permissionService.canWriteMarketing()">
      <div class="section-header">
        <h3><i class="fas fa-info-circle"></i> Collection Jobs</h3>
      </div>
      <div class="no-status" style="padding: 2rem; text-align: center; color: #6b7280;">
        <i class="fas fa-lock" style="font-size: 2rem; margin-bottom: 1rem; display: block; color: #d1d5db;"></i>
        <p style="font-weight: 600; margin-bottom: 0.25rem; color: #374151;">Read-Only Access</p>
        <p style="font-size: 0.875rem;">You have view-only access to marketing data. Running collection jobs requires a
          Telemarketer or Admin role.</p>
      </div>
    </div>

    <!-- Job Status Section -->
    <div class="status-section">
      <div class="section-header">
        <h3><i class="fas fa-info-circle"></i> Job Status</h3>
        <div class="status-actions" *ngIf="currentJobId">
          <button class="btn btn-secondary btn-sm" (click)="checkJobStatus()" [disabled]="checkingStatus">
            <i class="fas fa-spinner fa-spin" *ngIf="checkingStatus"></i>
            <i class="fas fa-sync-alt" *ngIf="!checkingStatus"></i>
            {{ checkingStatus ? 'Checking...' : 'Check Status' }}
          </button>
          <button class="btn btn-link btn-sm" (click)="clearJobIdFromStorage()" title="Clear Job ID">
            <i class="fas fa-times"></i> Clear
          </button>
        </div>
      </div>

      <div class="status-container" *ngIf="jobStatus; else noJobStatus">
        <div class="status-card">
          <div class="status-header">
            <div class="status-badge-wrapper">
              <span [class]="getStatusBadgeClass(jobStatus.status)">
                {{ jobStatus.status }}
              </span>
            </div>
            <div class="job-id">
              <strong>Job ID:</strong> {{ jobStatus.id }}
            </div>
          </div>

          <div class="status-details">
            <div class="detail-row" *ngIf="jobStatus.statusMessage">
              <label>Status Message:</label>
              <span>{{ jobStatus.statusMessage }}</span>
            </div>

            <div class="detail-row" *ngIf="jobStatus.placeType">
              <label>Place Type:</label>
              <span>{{ jobStatus.placeType }}</span>
            </div>

            <div class="detail-row" *ngIf="jobStatus.query">
              <label>Query:</label>
              <span>{{ jobStatus.query }}</span>
            </div>

            <div class="detail-row" *ngIf="jobStatus.latitude !== undefined && jobStatus.longitude !== undefined">
              <label>Location:</label>
              <span>{{ jobStatus.latitude | number:'1.6-6' }}, {{ jobStatus.longitude | number:'1.6-6' }}</span>
            </div>

            <div class="detail-row" *ngIf="jobStatus.radiusMeters">
              <label>Radius:</label>
              <span>{{ jobStatus.radiusMeters | number }} meters</span>
            </div>

            <div class="detail-row">
              <label>Fetch All Pages:</label>
              <span>{{ jobStatus.fetchAllPages ? 'Yes' : 'No' }}</span>
            </div>

            <div class="stats-grid">
              <div class="stat-item" *ngIf="jobStatus.pagesProcessed !== undefined">
                <div class="stat-value">{{ jobStatus.pagesProcessed }}</div>
                <div class="stat-label">Pages Processed</div>
              </div>
              <div class="stat-item" *ngIf="jobStatus.businessesFetched !== undefined">
                <div class="stat-value">{{ jobStatus.businessesFetched }}</div>
                <div class="stat-label">Businesses Fetched</div>
              </div>
              <div class="stat-item" *ngIf="jobStatus.businessesMutated !== undefined">
                <div class="stat-value">{{ jobStatus.businessesMutated }}</div>
                <div class="stat-label">Businesses Mutated</div>
              </div>
              <div class="stat-item" *ngIf="jobStatus.businessesSkipped !== undefined">
                <div class="stat-value">{{ jobStatus.businessesSkipped }}</div>
                <div class="stat-label">Businesses Skipped</div>
              </div>
            </div>

            <div class="timestamps">
              <div class="timestamp-item" *ngIf="jobStatus.createdAt">
                <label>Created:</label>
                <span>{{ formatDate(jobStatus.createdAt) }}</span>
              </div>
              <div class="timestamp-item" *ngIf="jobStatus.updatedAt">
                <label>Last Updated:</label>
                <span>{{ formatDate(jobStatus.updatedAt) }}</span>
              </div>
              <div class="timestamp-item" *ngIf="jobStatus.completedAt">
                <label>Completed:</label>
                <span>{{ formatDate(jobStatus.completedAt) }}</span>
              </div>
              <div class="timestamp-item" *ngIf="jobStatus.expiresAt">
                <label>Expires:</label>
                <span>{{ formatDate(jobStatus.expiresAt) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ng-template #noJobStatus>
        <div class="no-status">
          <p *ngIf="currentJobId">Job ID stored: {{ currentJobId }}</p>
          <p *ngIf="!currentJobId">No job status available. Start a collection job to see status information.</p>
        </div>
      </ng-template>
    </div>
  </div>
</div>